import type { Debt } from '@/types'
import type { IDebtRepository } from '@/repository/types'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, PromoJson, BalanceTransferOfferJson } from './schema'

type DebtRow = Database['public']['Tables']['debts']['Row']
type DebtInsert = Database['public']['Tables']['debts']['Insert']

/**
 * Cloud-backed debt repository.
 *
 * Implements `IDebtRepository` against Supabase Postgres. Same surface
 * the local-storage variant exposes, so stores/hooks switch over without
 * touching call sites — `bindReposToSession` swaps the singleton.
 *
 * RLS is the security floor: since v0.8.50 every query is implicitly
 * scoped to workspaces the caller is a member of (20261031_003). Reads
 * add an explicit `.eq('workspace_id', workspaceId)` so a household
 * member only ever loads the active workspace's rows.
 */
export class SupabaseDebtRepo implements IDebtRepository {
  private readonly client: SupabaseClient<Database>
  private readonly userId: string
  private readonly workspaceId: string
  /** v0.8.53 — set once a read shows migration 005's owner_user_id column. */
  private ownerColumnKnown = false

  constructor(client: SupabaseClient<Database>, userId: string, workspaceId: string) {
    this.client = client
    this.userId = userId
    this.workspaceId = workspaceId
  }

  async getAll(): Promise<Debt[]> {
    const { data, error } = await this.client
      .from('debts')
      .select('*')
      .eq('workspace_id', this.workspaceId)
      .order('created_at', { ascending: true })

    if (error) throw new Error(`[debts.getAll] ${error.message}`)
    this.noteColumns(data?.[0])
    return (data ?? []).map(rowToDebt)
  }

  private noteColumns(row: object | null | undefined): void {
    if (row && 'owner_user_id' in row) this.ownerColumnKnown = true
  }

  async getById(id: string): Promise<Debt | null> {
    const { data, error } = await this.client
      .from('debts')
      .select('*')
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)
      .maybeSingle()

    if (error) throw new Error(`[debts.getById] ${error.message}`)
    this.noteColumns(data)
    return data ? rowToDebt(data) : null
  }

  async upsert(debt: Debt): Promise<Debt> {
    const row = debtToInsert(debt, this.userId, this.workspaceId, this.ownerColumnKnown)
    const { data, error } = await this.client
      .from('debts')
      .upsert(row, { onConflict: 'id' })
      .select()
      .single()

    if (error) throw new Error(`[debts.upsert] ${error.message}`)
    this.noteColumns(data)
    return rowToDebt(data)
  }

  /**
   * v0.8.56 — UPDATE only the columns this edit changed. A full-row upsert
   * from a device that loaded earlier would revert a household member's
   * edits to the other fields (docs/product/concurrent-edits.md §1.3).
   * A column that was set before and is now omitted (e.g. `paid_off_at`
   * after Unmark) is written as NULL, so clears reach the cloud too.
   */
  async patch(before: Debt, after: Debt): Promise<Debt> {
    const changes = changedColumns(
      debtToInsert(before, this.userId, this.workspaceId, this.ownerColumnKnown),
      debtToInsert(after, this.userId, this.workspaceId, this.ownerColumnKnown)
    )
    if (Object.keys(changes).length === 0) return after
    const { data, error } = await this.client
      .from('debts')
      .update(changes)
      .eq('id', after.id)
      .eq('workspace_id', this.workspaceId)
      .select()
      .maybeSingle()

    if (error) throw new Error(`[debts.patch] ${error.message}`)
    if (!data) throw new Error('This debt was deleted on another device. Reload DebtOS.')
    this.noteColumns(data)
    return rowToDebt(data)
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.client
      .from('debts')
      .delete()
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)

    if (error) throw new Error(`[debts.delete] ${error.message}`)
  }

  /**
   * Truly replace — wipe all rows for this workspace, then insert.
   * Wrapped in best-effort sequencing rather than a transaction (PostgREST
   * doesn't expose explicit transactions; the only "transactional" path
   * would be a Postgres function). Acceptable for the migration UX
   * because the user is one device, online, single-shot.
   */
  async bulkReplace(debts: Debt[]): Promise<void> {
    const { error: delErr } = await this.client
      .from('debts')
      .delete()
      .eq('workspace_id', this.workspaceId)
    if (delErr) throw new Error(`[debts.bulkReplace:delete] ${delErr.message}`)

    if (debts.length === 0) return

    const rows = debts.map((d) =>
      debtToInsert(d, this.userId, this.workspaceId, this.ownerColumnKnown)
    )
    const { error: insErr } = await this.client.from('debts').insert(rows)
    if (insErr) throw new Error(`[debts.bulkReplace:insert] ${insErr.message}`)
  }

  async appendMany(debts: Debt[]): Promise<void> {
    // v0.8.44 — single INSERT statement. Postgres treats this as a
    // single transaction: either all rows land or none. Pre-fix the
    // Import dialog called bulkReplace([...existing, ...new]) which
    // wiped the workspace first; a 4xx on the insert pass (RLS, FK,
    // network) left the user with an empty registry. appendMany skips
    // the delete entirely, so partial-failure can only fail-closed
    // (nothing inserted) — never destroy existing data.
    if (debts.length === 0) return
    const rows = debts.map((d) =>
      debtToInsert(d, this.userId, this.workspaceId, this.ownerColumnKnown)
    )
    const { error } = await this.client.from('debts').insert(rows)
    if (error) throw new Error(`[debts.appendMany] ${error.message}`)
  }
}

// ─── Row ↔ entity mappers ────────────────────────────────────────────────

function rowToDebt(row: DebtRow): Debt {
  const debt: Debt = {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    lender: row.lender,
    type: row.type,
    balance: Number(row.balance),
    apr: Number(row.apr),
    minimumPayment: Number(row.minimum_payment),
    isInOptimizationEngine: row.is_in_optimization_engine,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
  if (row.due_day !== null) debt.dueDay = row.due_day
  if (row.notes !== null) debt.notes = row.notes
  if (row.promo) debt.promo = jsonToPromo(row.promo)
  if (row.balance_transfer_offer) {
    debt.balanceTransferOffer = jsonToBalanceTransferOffer(row.balance_transfer_offer)
  }
  if (row.paid_off_at !== null) debt.paidOffAt = row.paid_off_at
  if (row.owner_user_id) debt.ownerUserId = row.owner_user_id
  return debt
}

/**
 * @param ownerColumnKnown v0.8.53 — true once a read has shown the
 *   `owner_user_id` column exists (migration 005). Then the column is
 *   always written, so retagging a debt back to Joint sends `null`.
 *   Before that it's only written when a debt is explicitly tagged —
 *   same soft-migration posture as `paid_off_at` below, without that
 *   field's "can't clear" trade-off once the column is confirmed.
 */
/** Identity columns never change on edit. */
const IMMUTABLE_COLUMNS = new Set(['id', 'workspace_id', 'user_id', 'created_at'])

function changedColumns(before: DebtInsert, after: DebtInsert): Partial<DebtInsert> {
  const out: Record<string, unknown> = {}
  const b = before as Record<string, unknown>
  const a = after as Record<string, unknown>
  for (const key of new Set([...Object.keys(b), ...Object.keys(a)])) {
    if (IMMUTABLE_COLUMNS.has(key)) continue
    const next = key in a ? a[key] : null
    if (JSON.stringify(b[key] ?? null) !== JSON.stringify(next ?? null)) out[key] = next
  }
  return out as Partial<DebtInsert>
}

function debtToInsert(
  debt: Debt,
  userId: string,
  workspaceId: string,
  ownerColumnKnown = false
): DebtInsert {
  const insert: DebtInsert = {
    id: debt.id,
    workspace_id: workspaceId,
    user_id: userId,
    name: debt.name,
    lender: debt.lender,
    type: debt.type,
    balance: debt.balance,
    apr: debt.apr,
    minimum_payment: debt.minimumPayment,
    due_day: debt.dueDay ?? null,
    is_in_optimization_engine: debt.isInOptimizationEngine,
    promo: debt.promo ? promoToJson(debt.promo) : null,
    balance_transfer_offer: debt.balanceTransferOffer
      ? balanceTransferOfferToJson(debt.balanceTransferOffer)
      : null,
    notes: debt.notes ?? null,
    created_at: debt.createdAt,
    updated_at: debt.updatedAt,
  }
  // v0.8.42 — conditional include, returning to the v0.8.31
  // `balance_before_close` posture. v0.8.41 wrote paid_off_at
  // unconditionally so `unmarkPaidOff` could send null to clear it, but
  // that broke EVERY debt write on cloud projects without the
  // 20260617_002 migration — founder hit "Could not find the
  // 'paid_off_at' column…" trying to edit a debt amount. Regular debt
  // editing should not be gated on a schema migration the user hasn't
  // applied yet.
  //
  // Trade-off accepted: `unmarkPaidOff` no longer clears the cloud
  // column (the field is just dropped from the payload, so Postgres'
  // upsert-partial-set leaves the prior timestamp in place). Mark works
  // post-migration; unmark only fully syncs once the migration lands.
  // In-memory + localStorage both unmark cleanly regardless. Most users
  // will rarely unmark, and applying the migration restores full
  // bidirectional behavior.
  if (debt.paidOffAt !== undefined) insert.paid_off_at = debt.paidOffAt
  if (ownerColumnKnown || debt.ownerUserId !== undefined) {
    insert.owner_user_id = debt.ownerUserId ?? null
  }
  return insert
}

function jsonToPromo(p: PromoJson): NonNullable<Debt['promo']> {
  return {
    promoAPR: Number(p.promoAPR),
    promoExpiresAt: p.promoExpiresAt,
    postPromoAPR: Number(p.postPromoAPR),
  }
}

function promoToJson(p: NonNullable<Debt['promo']>): PromoJson {
  return {
    promoAPR: p.promoAPR,
    promoExpiresAt: p.promoExpiresAt,
    postPromoAPR: p.postPromoAPR,
  }
}

function jsonToBalanceTransferOffer(
  o: BalanceTransferOfferJson
): NonNullable<Debt['balanceTransferOffer']> {
  const out: NonNullable<Debt['balanceTransferOffer']> = {
    offerAPR: Number(o.offerAPR),
    offerValidUntil: o.offerValidUntil,
    postOfferAPR: Number(o.postOfferAPR),
    transferFeePct: Number(o.transferFeePct),
  }
  if (o.creditLimit !== undefined) out.creditLimit = Number(o.creditLimit)
  if (o.acceptedAt !== undefined) out.acceptedAt = o.acceptedAt
  return out
}

function balanceTransferOfferToJson(
  o: NonNullable<Debt['balanceTransferOffer']>
): BalanceTransferOfferJson {
  const out: BalanceTransferOfferJson = {
    offerAPR: o.offerAPR,
    offerValidUntil: o.offerValidUntil,
    postOfferAPR: o.postOfferAPR,
    transferFeePct: o.transferFeePct,
  }
  if (o.creditLimit !== undefined) out.creditLimit = o.creditLimit
  if (o.acceptedAt !== undefined) out.acceptedAt = o.acceptedAt
  return out
}
